/**
 * Reformatting the phone fields of a contact form before it is submitted.
 *
 * Numbers leave in the Stencil widget's international format
 * (`+91 76954 67223`), whatever the visitor typed. The anti-spam layer at the
 * edge refuses anything else with a 494 whose body reads like an outage, so a
 * visitor who typed a local number would be told to try again later for
 * something they could fix in a second.
 */
import { countryFromLocale, normalisePhone } from './phone';
import { warn } from '../log';

/** The first field that could not be understood; everything else went through. */
export interface PhoneFieldRejection {
  field: string;
  reason: 'invalid' | 'no_country';
}

/**
 * Rewrites each phone field of `input` in place, and reports the first one
 * that is not a usable number. A field whose number cannot be *checked* — no
 * library, because the CDN is blocked — is left as typed and allowed through,
 * which is what the standard widget does.
 */
export async function normalisePhoneFields(
  fields: Array<Record<string, unknown>>,
  input: Record<string, unknown>,
  detectedCountry: string | null,
  locale: string | undefined,
): Promise<PhoneFieldRejection | null> {
  const defaultCountry = detectedCountry ?? countryFromLocale(locale);

  for (const field of fields) {
    if (field.dataType !== 'PHONE' || typeof field.value !== 'string') continue;
    const key = field.value;
    const raw = input[key];
    if (typeof raw !== 'string' || !raw.trim()) continue;

    const result = await normalisePhone(raw, defaultCountry);
    // `=== false`, not `!`: this tsconfig has no strictNullChecks, and without
    // it TypeScript only narrows the union on an equality test.
    if (result.ok === false) {
      if (result.reason === 'unavailable') {
        warn('phone library unavailable; sending the number as typed', { field: key });
        continue;
      }
      return { field: key, reason: result.reason };
    }
    input[key] = result.formatted;
  }

  return null;
}
